import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime, formatMedida } from "@/lib/format";
import { sharePdf, logAcesso } from "@/lib/share";
import { Truck, X, Loader2, Share2, Printer, History } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal read-only de detalhe do romaneio. Aberto pelo histórico/rastreio.
 * Edição/cancelamento continuam exclusivos da página /romaneio.
 */
export function RomaneioDetalheModal({ romaneioId, open, onClose, zClass = "z-50" }) {
  const { hasPerm } = useAuth();
  const canPdf = hasPerm("romaneio:pdf");
  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!open || !romaneioId) {
      setDetalhe(null);
      return;
    }
    setLoading(true);
    api
      .get(`/romaneios/${encodeURIComponent(romaneioId)}`)
      .then((r) => setDetalhe(r.data))
      .catch((e) => {
        const _m = apiError(e);
        if (_m) toast.error(_m);
      })
      .finally(() => setLoading(false));
  }, [open, romaneioId]);

  const totais = useMemo(() => {
    const t = { kg: 0, m: 0 };
    (detalhe?.itens || []).forEach((i) => {
      if (i.unidade === "m") t.m += Number(i.peso || 0);
      else t.kg += Number(i.peso || 0);
    });
    return t;
  }, [detalhe]);

  const abrirPdf = () => {
    if (!detalhe) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/romaneios/${detalhe.id}/pdf`, "_blank");
    logAcesso({ tipo: "romaneio_impresso", documento_tipo: "romaneio", documento_id: detalhe.id, documento_codigo: detalhe.codigo, ref: detalhe.ref });
  };

  const compartilhar = async () => {
    if (!detalhe) return;
    setSharing(true);
    try {
      await sharePdf(`/romaneios/${detalhe.id}/pdf`, `Romaneio - REF ${detalhe.ref}.pdf`, `Romaneio REF ${detalhe.ref}`);
      logAcesso({ tipo: "romaneio_compartilhado", documento_tipo: "romaneio", documento_id: detalhe.id, documento_codigo: detalhe.codigo, ref: detalhe.ref });
    } finally {
      setSharing(false);
    }
  };

  if (!open) return null;

  const STATUS_CLS = {
    Pendente: "border-amber-300 bg-amber-50 text-amber-700",
    "Em separação": "border-blue-300 bg-blue-50 text-blue-700",
    Finalizado: "border-green-300 bg-green-50 text-green-700",
    Cancelado: "border-red-300 bg-red-50 text-red-700",
  };

  return (
    <div
      className={`fixed inset-0 ${zClass} flex items-center justify-center bg-black/50 p-4`}
      onClick={onClose}
      data-testid="romaneio-detalhe-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-md bg-white shadow-xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b-2 border-slate-100 bg-white px-5 py-3">
          <div className="flex items-center gap-2">
            <Truck size={18} className="text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900">
              {detalhe ? `Romaneio ${detalhe.codigo}` : "Carregando romaneio..."}
            </h2>
            {detalhe?.status && (
              <span className={`ml-2 rounded-md border-2 px-2 py-0.5 text-xs font-semibold ${STATUS_CLS[detalhe.status] || "border-slate-300 bg-slate-100 text-slate-700"}`}>
                {detalhe.status}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="romaneio-modal-close"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          {loading && !detalhe && (
            <div className="flex justify-center py-10 text-slate-400">
              <Loader2 className="animate-spin" />
            </div>
          )}
          {detalhe && (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">REF</div>
                  <div className="font-mono text-lg font-bold text-slate-900">{detalhe.ref || "—"}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">Cliente</div>
                  <div className="font-semibold text-slate-800">{detalhe.cliente || "—"}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">Destino</div>
                  <div className="font-semibold text-slate-800">{detalhe.destino || "—"}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">Data</div>
                  <div className="font-semibold text-slate-800">{formatDateTime(detalhe.data)}</div>
                </div>
              </div>

              {detalhe.descricao_peca && (
                <div className="mt-3 text-sm text-slate-600">
                  <span className="font-semibold">Descrição da peça:</span> {detalhe.descricao_peca}
                </div>
              )}
              {detalhe.observacao && (
                <div className="mt-1 text-sm text-slate-600">
                  <span className="font-semibold">Observação:</span> {detalhe.observacao}
                </div>
              )}

              <div className="mt-4 rounded-md border-2 border-slate-200 bg-slate-50">
                <div className="border-b-2 border-slate-200 px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-600">
                  Rolos que saíram ({detalhe.total_rolos || 0})
                </div>
                <ul className="divide-y divide-slate-200">
                  {(detalhe.itens || []).map((i) => (
                    <li key={i.rolo_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`romaneio-modal-rolo-${i.codigo}`}>
                      <span className="min-w-0">
                        <span className="font-mono text-xs text-slate-400">{i.codigo} </span>
                        {i.tipo ? `${i.tipo} ${i.cor}` : i.cor} <span className="text-slate-400">({i.fornecedor_nome})</span>
                        {i.bloco === "ribana" && <span className="ml-1 text-xs text-amber-600">· ribana</span>}
                      </span>
                      <span className="font-mono font-semibold text-slate-800">{formatMedida(i.peso, i.unidade)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {(totais.kg > 0 || totais.m > 0) && (
                <div
                  data-testid="romaneio-modal-total"
                  className="mt-3 flex flex-wrap items-center justify-end gap-x-5 gap-y-1 rounded-md border-2 border-slate-900 bg-slate-900 px-4 py-3 text-white"
                >
                  {totais.kg > 0 && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-300">Total:</span>
                      <span className="font-mono text-lg font-bold">{totais.kg.toFixed(2).replace(".", ",")} kg</span>
                    </div>
                  )}
                  {totais.m > 0 && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-300">Total:</span>
                      <span className="font-mono text-lg font-bold">{totais.m.toFixed(2).replace(".", ",")} m</span>
                    </div>
                  )}
                </div>
              )}

              {(detalhe.historico_edicoes || []).length > 0 && (
                <details className="mt-3 rounded-md border-l-4 border-blue-300 bg-blue-50/50 p-2 text-xs" data-testid="romaneio-modal-hist-edicoes">
                  <summary className="flex cursor-pointer items-center gap-1 font-semibold text-slate-700">
                    <History size={12} /> Histórico de edições ({detalhe.historico_edicoes.length})
                  </summary>
                  <ul className="mt-2 space-y-2 pl-1">
                    {detalhe.historico_edicoes.slice().reverse().map((ev, idx) => (
                      <li key={idx} className="border-l-2 border-blue-200 pl-2">
                        <div className="text-slate-500">
                          {formatDateTime(ev.data_hora)} · <strong className="text-slate-800">{ev.usuario_nome}</strong>
                        </div>
                        <ul className="mt-0.5 space-y-0.5">
                          {(ev.mudancas || []).map((m, i) => (
                            <li key={i} className="text-slate-600">
                              <span className="font-semibold capitalize">{m.campo === "observacao" ? "obs." : m.campo}:</span>{" "}
                              <span className="text-slate-400 line-through">{m.antes || "—"}</span>
                              {" → "}
                              <span className="font-semibold text-slate-800">{m.depois || "—"}</span>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {canPdf && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={abrirPdf}
                    data-testid="romaneio-modal-pdf"
                    className="flex items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800"
                  >
                    <Printer size={16} /> PDF
                  </button>
                  <button
                    type="button"
                    onClick={compartilhar}
                    disabled={sharing}
                    data-testid="romaneio-modal-share"
                    className="flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
                    Compartilhar
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
